import axios from "axios";
import { apiUrl } from "../common/apiURLs";
import { clearStorage, getStorage } from "../common/storageHelper";
import { customerInfoIdValue, tokenValue } from "../common/helperConstants";
import { getInviteId } from "../common/helperMethods";

const API_BASE_URL = `${apiUrl}/api`;

const customerInfoId = getInviteId();
// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    ...(customerInfoId && { "X-Cust-Id": customerInfoId }),
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = getStorage(tokenValue);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearStorage(tokenValue);
      clearStorage(customerInfoIdValue);
      // window.location.href = "/";
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authApi = {
  verifyOtp: async (inviteId: string, otpCode: number) => {
    const response = await api.post("/auth/verify-otp", { inviteId, otpCode });
    return response.data;
  },
  logout: async () => {
    await api.post("/auth/logout");
    clearStorage(tokenValue);
    clearStorage(customerInfoIdValue);
  },
};

// Invites API
export const invitesApi = {
  resendOtp: async (inviteId: string) => {
    const response = await api.post(`/invites/${inviteId}/resend`);
    return response.data;
  },
};

// Applications API
export const applicationsApi = {
  getApplication: async (customerInfoId: string) => {
    const response = await api.get(`/applications/${customerInfoId}`);
    return response.data;
  },

  uploadDocument: async (
    customerInfoId: string,
    documentType: string,
    file: File,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentType", documentType);

    const response = await api.post(
      `/applications/${customerInfoId}/documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },

  saveReferences: async (customerInfoId: string, references: any[]) => {
    const response = await api.put(
      `/applications/${customerInfoId}/references`,
      references,
    );
    return response.data;
  },
  validatePhone: async (customerInfoId: string, phoneNumber: string) => {
    const response = await api.post(
      `/applications/${customerInfoId}/validatephone`,
      { PhoneNumber: phoneNumber },
    );
    return response.data as { isPhoneNoValid: boolean; remarks?: string };
  },

  saveQuestionnaire: async (customerInfoId: string, questionnaire: any) => {
    const response = await api.put(
      `/applications/${customerInfoId}/questionnaire`,
      questionnaire,
    );
    return response.data;
  },

  createPlaidLinkToken: async (
    applicationId: string,
    connectionType?: string,
  ) => {
    const response = await api.post(
      `/applications/${applicationId}/plaid/link-token`,
      { connectionType: connectionType },
    );
    return response.data;
  },

  exchangePlaidToken: async (
    customerInfoId: string,
    publicToken: string,
    connectionType: string,
    sessionId: string | null,
  ) => {
    const response = await api.post(
      `/applications/${customerInfoId}/plaid/exchange-token`,
      {
        publicToken,
        connectionType: connectionType,
        sessionId,
      },
    );
    return response.data;
  },

  getStatus: async (customerInfoId: string) => {
    const response = await api.get(`/applications/${customerInfoId}/status`);
    return response.data;
  },

  exchangeCanopyInsurance: async (customerInfoId: string, model: any) => {
    const response = await api.post(
      `/applications/${customerInfoId}/canopy/retrieve-insurance`,
      model,
    );
    return response.data;
  },

  scanDL: async (customerInfoId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post(
      `/applications/${customerInfoId}/scan`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },

  logPlaidLinkEvent: async (
    infoId: string,
    payload: {
      sessionId: string | null;
      eventName: string;
      viewName?: string | null;
      errorType?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
      institutionId?: string | null;
      institutionName?: string | null;
      linkSessionId?: string | null;
      requestId?: string | null;
      timestamp: string;
      rawMetadata?: string;
    },
  ) => {
    return api.post(`/applications/${infoId}/plaid/link-event`, payload);
  },
};

export default api;
